import React, { useState, useEffect, createRef } from 'react';
import PropTypes, { oneOfType, oneOf } from 'prop-types';
import { Icon } from '@ui5/webcomponents-react';
import throttle from 'lodash.throttle';
import { setStylesScreen } from './FlexibleColumnsHelpers';

const SmallBegin = 'small-begin';
const StandardTransition = 'all .1s ease-in';
const LargeBegin = 'large-begin';

const handleBeginPercentage = (endFixedWidth, percentageWidth, splitWidth) => {
  if (parseFloat(endFixedWidth) === 0) {
    return `calc(${percentageWidth}% - ${splitWidth}px)`;
  } else {
    if (typeof endFixedWidth === 'number') {
      return `calc(${percentageWidth}% - ${splitWidth}px - ${endFixedWidth}px)`;
    } else if (
      typeof endFixedWidth === 'string' &&
      endFixedWidth.trim() === parseFloat(endFixedWidth) + ''
    ) {
      return `calc(${percentageWidth}% - ${splitWidth}px - ${endFixedWidth}px)`;
    } else {
      return `calc(${percentageWidth}% - ${splitWidth}px - ${endFixedWidth})`;
    }
  }
};

const handleEndPercentage = (beginWidth, percentageWidth, splitWidth) => {
  if (parseFloat(beginWidth) === 0) {
    return `calc(${percentageWidth}% - ${splitWidth}px)`;
  } else {
    if (typeof beginWidth === 'number') {
      return `calc(${percentageWidth}% - ${splitWidth}px - ${beginWidth}px)`;
    } else if (
      typeof beginWidth === 'string' &&
      beginWidth.trim() === parseFloat(beginWidth) + ''
    ) {
      return `calc(${percentageWidth}% - ${splitWidth}px - ${beginWidth}px)`;
    } else {
      return `calc(${percentageWidth}% - ${splitWidth}px - ${beginWidth})`;
    }
  }
};

export const largeScreenMidWidthCalculator = (beginWidth, endFixedWidth, splitWidth) => {
  let percentageWidth = 100;
  let beginIsPercentage;
  let endIsPercentage;
  // beginWidth use percentage like '33%'
  if (typeof beginWidth === 'string' && beginWidth.trim().endsWith('%')) {
    beginIsPercentage = true;
    percentageWidth = percentageWidth - parseFloat(beginWidth);
  } else {
    beginIsPercentage = false;
  }
  // endFixedWidth use percentage like '33%'
  if (typeof endFixedWidth === 'string' && endFixedWidth.trim().endsWith('%')) {
    endIsPercentage = true;
    percentageWidth = percentageWidth - parseFloat(endFixedWidth);
  } else {
    endIsPercentage = false;
  }
  if (beginIsPercentage && endIsPercentage) {
    return `calc(${percentageWidth}% - ${splitWidth}px)`;
  } else if (beginIsPercentage) {
    return handleBeginPercentage(endFixedWidth, percentageWidth, splitWidth);
  } else if (endIsPercentage) {
    return handleEndPercentage(beginWidth, percentageWidth, splitWidth);
  }
};
export const smallScreenWidthWithSplitCalculation = (endFixedWidth, splitWidth) => {
  let percentageWidth = 100;
  let endIsPercentage;
  // endFixedWidth use percentage like '33%'
  if (typeof endFixedWidth === 'string' && endFixedWidth.trim().endsWith('%')) {
    endIsPercentage = true;
    percentageWidth = percentageWidth - parseFloat(endFixedWidth);
  } else {
    endIsPercentage = false;
  }
  if (endIsPercentage) {
    return `calc(${percentageWidth}% - ${splitWidth}px)`;
  } else {
    if (parseFloat(endFixedWidth) === 0) {
      return `calc(${percentageWidth}% - ${splitWidth}px)`;
    } else {
      if (typeof endFixedWidth === 'number') {
        return `calc(${percentageWidth}% - ${splitWidth}px - ${endFixedWidth}px)`;
      } else if (
        typeof endFixedWidth === 'string' &&
        endFixedWidth.trim() === parseFloat(endFixedWidth) + ''
      ) {
        return `calc(${percentageWidth}% - ${splitWidth}px - ${endFixedWidth}px)`;
      } else {
        return `calc(${percentageWidth}% - ${splitWidth}px - ${endFixedWidth})`;
      }
    }
  }
};

export const doClick = (disableSplit, mode, setMode) => {
  if (disableSplit) {
    return;
  }
  if (mode === SmallBegin) {
    setMode(LargeBegin);
  } else if (mode === LargeBegin) {
    setMode(SmallBegin);
  }
};

const splitTrackerStyles = ({
  disableSplit,
  splitTrackerStylesForDefault,
  splitTrackerStylesForModes,
  mode,
}) => {
  return {
    width: '1.5rem',
    height: '1.5rem',
    padding: 0,
    transition: StandardTransition,
    marginLeft: '-0.25rem',
    marginRight: '-0.25rem',
    display: 'inline-block',
    verticalAlign: 'middle',
    position: 'relative',
    overflow: 'visible',
    margin: 'auto',
    backgroundColor: 'transparent',
    opacity: disableSplit ? 0.1 : 1,
    border: 'none',
    ...(splitTrackerStylesForDefault || {}),
    ...(splitTrackerStylesForModes[mode] || {}),
  };
};

const splitContainerStyles = ({
  startzIndex,
  mode,
  splitStylesForDefault,
  splitStylesForModes,
}) => {
  return {
    background: 'linear-gradient(to bottom, #dfe3e4, #f3f4f5)',
    position: 'relative',
    verticalAlign: 'middle',
    height: '100%',
    zIndex: startzIndex,
    cursor: 'pointer',
    display: mode.startsWith('fullscreen') ? 'none' : 'flex',
    ...(splitStylesForDefault || {}),
    ...(splitStylesForModes[mode] || {}),
  };
};

const doEffect = ({
  mode,
  beginRef,
  midRef,
  endRef,
  splitRef,
  layoutRef,
  forceMinWidth,
  screenThreshold,
  setStylesForLargeSmallScreens,
}) => {
  const currentWidth = layoutRef.current.offsetWidth;
  const modes =
    currentWidth > screenThreshold
      ? setStylesForLargeSmallScreens('modesForLargeScreen')
      : setStylesForLargeSmallScreens('modesForSmallScreen');
  const modeStyle = modes[mode] ? modes[mode] : modes[SmallBegin];
  const { begin, mid, end, split } = modeStyle;
  const applyDomStyles = (styles, dom) => {
    if (styles && dom) {
      Object.keys(styles).forEach(key => {
        if (forceMinWidth) {
          dom.style[key] = styles[key];
        } else {
          key !== 'minWidth' && (dom.style[key] = styles[key]);
        }
      });
    }
  };
  applyDomStyles(begin, beginRef.current);
  applyDomStyles(mid, midRef.current);
  applyDomStyles(end, endRef.current);
  applyDomStyles(split, splitRef.current);
};

const endColumnContainerStyle = ({ componentStyles, endFixedWidth, startzIndex }) => {
  return {
    ...componentStyles,
    width: endFixedWidth,
    zIndex: parseFloat(endFixedWidth) <= 0 ? startzIndex : startzIndex + 1,
  };
};

const FlexibleColumnLayout = ({
  componentDefaultStyles = {},
  componentAnimationStyles = {
    transition: 'width 560ms cubic-bezier(.1, 0, .05, 1),visibility 560ms ease-in',
  },
  startzIndex = 1,
  screenThreshold = 1000,
  mode = SmallBegin,
  setMode,
  disableSplit = false,
  splitComponent = null,
  splitWidth = 24,
  splitStylesForDefault = {},
  splitStylesForModes = {},
  splitTrackerStylesForDefault = {},
  splitTrackerStylesForModes = {},
  beginColumnPage = null,
  midColumnPage = null,
  endColumnPage = null,
  smallBeginWidth = '33%',
  largeBeginWidth = '67%',
  endFixedWidth = '0',
  forceMinWidth = false,
  withAnimation = true,
}) => {
  console.warn(
    "Deprecated. Please use '@ui5/webcomponents-react/dist/FlexibleColumnLayout' instead.",
  );

  const [resizeTicket, setResizeTicket] = useState(0);
  const largeScreenLargeBeginMidWidth = largeScreenMidWidthCalculator(
    largeBeginWidth,
    endFixedWidth,
    splitWidth,
  );
  const largeScreenSmallBeginMidWidth = largeScreenMidWidthCalculator(
    smallBeginWidth,
    endFixedWidth,
    splitWidth,
  );
  const smallScreenWithSplitWidth = smallScreenWidthWithSplitCalculation(endFixedWidth, splitWidth);

  const splitRef = createRef();
  const beginRef = createRef();
  const midRef = createRef();
  const endRef = createRef();
  const layoutRef = createRef();

  const componentStyles = {
    ...componentDefaultStyles,
    ...(withAnimation ? componentAnimationStyles : {}),
  };

  const throttledResizeHandler = throttle(
    () => {
      clearTimeout(resizeTicket);
      const ticket = setTimeout(() => {
        setResizeTicket(ticket);
      }, 500);
    },
    500,
    {},
  );

  const defaultSplitComponent = (
    <div
      className={'split-container'}
      data-testid={'splitter'}
      ref={splitRef}
      disabled={disableSplit}
      style={splitContainerStyles({
        startzIndex,
        mode,
        splitStylesForDefault,
        splitStylesForModes,
      })}
    >
      <div
        className={'split-tracker'}
        data-testid={'split-tracker'}
        title="Expand the first column"
        style={splitTrackerStyles({
          disableSplit,
          splitTrackerStylesForDefault,
          splitTrackerStylesForModes,
          mode,
        })}
        onClick={() => doClick(disableSplit, mode, setMode)}
      >
        <span>
          <div
            style={{
              backgroundImage: 'linear-gradient(to top, #0854a0, rgba(8,84,160,0))',
              backgroundPositionY: '-0.3125rem',
              content: '',
              height: '4rem',
              width: '100%',
              transition: StandardTransition,
              backgroundRepeat: 'no-repeat',
              backgroundSize: '0.0625rem 100%',
              backgroundPositionX: 'calc(49.96875%)',
            }}
          ></div>
          <Icon
            className={'split-arrow'}
            style={{ fontFamily: 'SAP-icons', color: '#0854a0', marginLeft: '4px' }}
            name={mode === SmallBegin ? 'slim-arrow-right' : 'slim-arrow-left'}
          />
          <div
            style={{
              backgroundImage: 'linear-gradient(to bottom, #0854a0, rgba(8,84,160,0))',
              backgroundPositionY: '-0.3125rem',
              content: '',
              height: '4rem',
              width: '100%',
              transition: StandardTransition,
              backgroundRepeat: 'no-repeat',
              backgroundSize: '0.0625rem 100%',
              backgroundPositionX: 'calc(49.96875%)',
            }}
          ></div>
        </span>
      </div>
    </div>
  );

  const setStylesForLargeSmallScreens = inputMode => {
    return setStylesScreen({
      mode: inputMode,
      startzIndex,
      largeScreenLargeBeginMidWidth,
      largeBeginWidth,
      endFixedWidth,
      smallBeginWidth,
      largeScreenSmallBeginMidWidth,
      smallScreenWithSplitWidth,
    });
  };

  useEffect(() => {
    doEffect({
      mode,
      beginRef,
      midRef,
      endRef,
      splitRef,
      layoutRef,
      forceMinWidth,
      screenThreshold,
      setStylesForLargeSmallScreens,
    });
    window.addEventListener('resize', throttledResizeHandler);
    return () => {
      window.removeEventListener('resize', throttledResizeHandler);
    };
  });
  return (
    <div
      ref={layoutRef}
      data-testid="layoutRef"
      className="eureka-flexible-column-layout"
      style={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}
    >
      <div
        className={'begin-column-container'}
        ref={beginRef}
        data-testid={'begin-column'}
        style={{ ...componentStyles, width: 0, backgroundColor: '#f7f7f7' }}
      >
        {beginColumnPage}
      </div>
      {splitComponent || defaultSplitComponent}
      <div
        className={'mid-column-container'}
        data-testid="midColumn"
        ref={midRef}
        style={{ ...componentStyles, width: 'auto', flexGrow: 1 }}
      >
        {midColumnPage}
      </div>
      <div
        className={'end-column-container'}
        data-testid={'lastColumn'}
        ref={endRef}
        style={endColumnContainerStyle({ componentStyles, endFixedWidth, startzIndex })}
      >
        {endColumnPage}
      </div>
    </div>
  );
};

FlexibleColumnLayout.displayName = 'FlexibleColumnLayout';

FlexibleColumnLayout.propTypes = {
  componentDefaultStyles: PropTypes.object,
  startzIndex: PropTypes.number,
  screenThreshold: PropTypes.number,
  mode: oneOf([SmallBegin, LargeBegin, 'fullscreen-begin', 'fullscreen-mid', 'fullscreen-end']),
  setMode: PropTypes.func,
  disableSplit: PropTypes.bool,
  splitComponent: PropTypes.node,
  splitWidth: PropTypes.number,
  splitStylesForDefault: PropTypes.object,
  splitStylesForModes: PropTypes.object,
  splitTrackerStylesForDefault: PropTypes.object,
  splitTrackerStylesForModes: PropTypes.object,
  beginColumnPage: PropTypes.node,
  midColumnPage: PropTypes.node,
  endColumnPage: PropTypes.node,
  smallBeginWidth: oneOfType([PropTypes.string, PropTypes.number]),
  largeBeginWidth: oneOfType([PropTypes.string, PropTypes.number]),
  endFixedWidth: oneOfType([PropTypes.string, PropTypes.number]),
  forceMinWidth: PropTypes.bool,
};

FlexibleColumnLayout.propDescriptions = {
  componentDefaultStyles:
    'Change the default value only if you know what you are doing. It should provide default styles for each component. The default value provides transition style, if removed or changed, the transtion will be removed or changed. You can add any styles for component here if you know what you are doing',
  startzIndex:
    'A zIndex to make sure overlap z-index for three pages. Which one(s) is active, it will be startzIndex + 1',
  screenThreshold:
    "when to switch to a small screen mode, size <= screenThreshold + 'px' will trigger",
  mode: "Mode of layout, oen of 'small-begin', 'large-begin', 'fullscreen-begin', 'fullscreen-mid', 'fullscreen-end'",
  setMode: 'Function to change mode, pass from parent',
  disableSplit: 'Disable split function and show grey out styles or not',
  splitComponent:
    'split component, between begin column component and middle column component, default null, if null, render a default node defined inside layout',
  splitWidth:
    "width in px to define the width of split component. Default is 24(px) means the default node for split component. Don't change this value unless you know what you are doing.",
  splitStylesForDefault:
    'split styles for default split component node. Will be ignore if you use own defined split node.',
  splitStylesForModes:
    'modes styles for split component node. You can provide object with key of modes and give them styles. Will be ignore if you use own defined split node.',
  splitTrackerStylesForDefault:
    'split styles for default split component node tracker. Will be ignore if you use own defined split node.',
  splitTrackerStylesForModes:
    'modes styles for split component node tracker. You can provide object with key of modes and give them styles. Will be ignore if you use own defined split node.',
  beginColumnPage: 'Begin Component for the left area',
  midColumnPage: 'Begin Component for the mid(right) area',
  endColumnPage:
    'Begin Component for the right(end) area. If this be null(be default), mid will be right.',
  smallBeginWidth:
    'Width value for small begin, any valid width value accpetable. Must be valid for CSS width value',
  largeBeginWidth:
    'Width value for large begin, any valid width value accpetable. Must be valid for CSS width value',
  endFixedWidth:
    'Width value for end. It must be a fixed value, in px, pt, percentage, any kind. So end component will be fixed in given size. Must be valid for CSS width value',
  forceMinWidth:
    "Sometimes even you set the with of begin, it won't work because of html layout render, so set min-width to same value of width will be neccessary, set this to true to enable min-width=same value of width",
};

export default FlexibleColumnLayout;
